import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { CSVLink } from 'react-csv'
import NumberFormat from 'react-number-format'
import { DataTable } from '../../../../midasComponents'
import { getText } from '../../../../internal/TextLabels'

const getName = name => {
  // Formula injection prevention, add apostrophe before possible bad character
  const letter = name.charAt(0)
  if (letter === '=' || letter === '@' || letter === '-' || letter === '+')
    return `’${name}`
  return name
}

const formattingCvsFile = topList => {
  return topList.map(topListRow => {
    const row = {}
    row[getText('statistics.csv.position')] = topListRow.position
    row[getText('statistics.csv.name')] = getName(topListRow.name)
    row[getText('statistics.csv.newOrders')] = topListRow.applicationCount
    row[getText('statistics.csv.reservationsCount')] =
      topListRow.authorizationCount
    row[getText('statistics.csv.transactionsCount')] =
      topListRow.transactionCount
    row[getText('statistics.csv.reservationsAmount')] =
      topListRow.authorizationAmount
    row[getText('statistics.csv.transactionAmount')] =
      topListRow.transactionAmount
    return row
  })
}

class PaginatedTopList extends Component {
  static chunkList(chunkSize, data) {
    const R = []
    for (let i = 0; i < data.length; i += chunkSize)
      R.push(data.slice(i, i + chunkSize))
    return R
  }

  constructor(props) {
    super(props)

    const defaultPageSize = 10
    const chunkedData = PaginatedTopList.chunkList(defaultPageSize, props.data)

    this.state = {
      lastSort: {
        headerName: '',
        direction: '',
      },
      currentPage: 0,
      pageSize: defaultPageSize,
      pagedData: chunkedData,
      totalPages: chunkedData.length,
    }
  }

  UNSAFE_componentWillReceiveProps(next) { //eslint-disable-line
    const { pageSize } = this.state
    const chunkedData = PaginatedTopList.chunkList(pageSize, next.data)
    this.setState({
      pagedData: chunkedData,
      currentPage: 0,
      totalPages: chunkedData.length,
    })
  }

  onPageSizeChanged(size) {
    const chunkedData = PaginatedTopList.chunkList(size, this.props.data)
    this.setState({
      pageSize: size,
      pagedData: chunkedData,
      currentPage: 0,
      totalPages: chunkedData.length,
    })
  }

  onNext() {
    if (this.state.currentPage < this.state.totalPages - 1) {
      this.setState(state => ({ currentPage: state.currentPage + 1 }))
    }
  }

  onPrev() {
    if (this.state.currentPage > 0) {
      this.setState(state => ({ currentPage: state.currentPage - 1 }))
    }
  }

  onSortChange(newHeaderName, newDirection) {
    if (
      this.state.lastSort.headerName !== newHeaderName ||
      this.state.lastSort.direction !== newDirection
    ) {
      const sorted = this.props.data.concat().sort((lhs, rhs) => {
        if (newDirection === 'desc') {
          return lhs[newHeaderName] > rhs[newHeaderName] ? 1 : -1
        }
        if (newDirection === 'asc') {
          return lhs[newHeaderName] < rhs[newHeaderName] ? 1 : -1
        }
        return 1
      })
      const { pageSize } = this.state
      const chunkedData = PaginatedTopList.chunkList(pageSize, sorted)

      this.setState({
        lastSort: {
          headerName: newHeaderName,
          direction: newDirection,
        },
        pagedData: chunkedData,
        currentPage: 0,
        totalPages: chunkedData.length,
      })
    }
  }

  render() {
    const tableRows = this.state.pagedData[this.state.currentPage].map(row => {
      return (
        <tr key={row.position} style={{ cursor: 'auto' }}>
          <td>{row.position}</td>
          <td>
              {row.name.includes('${unknown}') // eslint-disable-line
              ? getText('default.unknown')
              : row.name}
          </td>
          <td style={{ textAlign: 'right', paddingTop: '2rem' }}>
            <NumberFormat
              value={row.applicationCount}
              displayType="text"
              thousandSeparator={getText('default.thousandSeparator')}
              suffix={` ${getText('default.pcs')}`}
            />
          </td>
          <td style={{ textAlign: 'right', paddingTop: '2rem' }}>
            <NumberFormat
              value={row.authorizationCount}
              displayType="text"
              thousandSeparator={getText('default.thousandSeparator')}
              suffix={` ${getText('default.pcs')}`}
            />
            {' / '}
            <NumberFormat
              value={row.transactionCount}
              displayType="text"
              thousandSeparator={getText('default.thousandSeparator')}
              suffix={` ${getText('default.pcs')}`}
            />
          </td>
          <td style={{ textAlign: 'right', paddingTop: '2rem' }}>
            <NumberFormat
              value={row.authorizationAmount}
              displayType="text"
              thousandSeparator={getText('default.thousandSeparator')}
              renderText={val => getText('default.currency', val)}
            />
          </td>
          <td style={{ textAlign: 'right', paddingTop: '2rem' }}>
            <NumberFormat
              value={row.transactionAmount}
              displayType="text"
              thousandSeparator={getText('default.thousandSeparator')}
              renderText={val => getText('default.currency', val)}
            />
          </td>
        </tr>
      )
    })

    return (
      <div>
        <DataTable
          hover
          wrap
          fixed
          disableRowActions
          rowsPerPageTitle={getText('default.rowsPerPage')}
          pagination={{
            currentPage: this.state.currentPage + 1,
            totalPages: this.state.totalPages,
            rowsPerPage: Number(this.state.pageSize),
          }}
          lastSort={{
            headerName: this.state.lastSort.headerName,
            direction: this.state.lastSort.direction,
          }}
          onNext={() => this.onNext()}
          onPrev={() => this.onPrev()}
          onRowsPerPageChange={size => this.onPageSizeChanged(size)}
          onSort={(currentHeaderId, direction) =>
            this.onSortChange(currentHeaderId, direction)
          }
          headers={[
            {
              id: 'position',
              label: '#',
              nonSortable: false,
            },
            {
              id: 'name',
              label: getText('statistics.name'),
              nonSortable: false,
            },
            {
              id: 'applicationCount',
              type: 'number',
              label: getText('statistics.applicationCount'),
              tooltip: getText('statistics.tooltip.totalApplication'),
              nonSortable: false,
            },
            {
              id: 'transactionCount',
              type: 'number',
              label: getText('statistics.transactionCount'),
              tooltip: getText('statistics.tooltip.totalFirstTimePurchase'),
              nonSortable: true,
            },
            {
              id: 'authorizationAmount',
              type: 'number',
              label: getText('statistics.transactionReserved'),
              tooltip: getText('statistics.tooltip.totalRevenueReserved'),
              nonSortable: false,
            },
            {
              id: 'transactionAmount',
              type: 'number',
              label: getText('statistics.transactionBooked'),
              tooltip: getText('statistics.tooltip.totalRevenueBooked'),
              nonSortable: false,
            },
          ]}
        >
          <tbody>{tableRows}</tbody>
        </DataTable>
        <div
          style={{ textAlign: 'right', fontSize: '1rem', margin: '2rem 0 0' }}
        >
          <CSVLink
            data={formattingCvsFile(this.props.data)}
            filename="barchart.csv"
            target="_blank"
          >
            {getText('statistics.getCSV')}
          </CSVLink>
        </div>
      </div>
    )
  }
}

PaginatedTopList.propTypes = {
  data: PropTypes.array.isRequired,
}

export default PaginatedTopList
